import type { ReactNode } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";

interface MultiSelectFilterProps<T extends string> {
  allLabel: string;
  getOptionLabel?: (option: T) => string;
  onChange: (value: T[]) => void;
  options: readonly T[];
  renderOption?: (option: T) => ReactNode;
  value: T[];
}

/**
 * A searchable multi-select filter over a fixed list of string values. Nothing picked means
 * "no filter", shown as `allLabel`. `getOptionLabel` is the text an option is searched by;
 * `renderOption` draws it richer (e.g. a chip) in the menu and the field.
 */
export const MultiSelectFilter = <T extends string>(props: MultiSelectFilterProps<T>) => {
  const { allLabel, getOptionLabel, onChange, options, renderOption, value } = props;
  let placeholder = "";
  if (value.length === 0) placeholder = allLabel;

  const toLabel = (option: T): string => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    return option;
  };

  const toContent = (option: T): ReactNode => {
    if (renderOption) {
      return renderOption(option);
    }
    return toLabel(option);
  };

  const handleChange = (_event: unknown, next: T[]) => {
    onChange(next);
  };

  return (
    <Autocomplete
      disableCloseOnSelect
      fullWidth
      getOptionLabel={toLabel}
      multiple
      onChange={handleChange}
      options={options}
      size="small"
      value={value}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          gap: 0.5,
        },
        ...(value.length > 0 && {
          "& .MuiAutocomplete-inputRoot:not(.Mui-focused) .MuiAutocomplete-input": {
            minWidth: 0,
            px: 0,
            width: 0,
          },
        }),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
        />
      )}
      renderOption={(optionProps, option, state) => {
        const { key, ...liProps } = optionProps;
        return (
          <li
            key={key}
            {...liProps}
          >
            <Checkbox
              checked={state.selected}
              size="small"
              sx={{
                ml: -1,
                py: 0,
              }}
            />
            {toContent(option)}
          </li>
        );
      }}
      renderValue={(selected) =>
        selected.map((option) => (
          <Box
            key={option}
            sx={{ display: "inline-flex" }}
          >
            {toContent(option)}
          </Box>
        ))}
    />
  );
};
